import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@mlm/ui';
import { EARNINGS_SUB_BUCKET_KINDS } from '@mlm/shared';
import type { EarningsBreakdownEntry } from '../../lib/api';
import { getLedger } from '../../lib/api';
import { fmtCurrency, fmtNumber } from '../../lib/format';
import { StatementList } from './statement-list';

/**
 * The "Earnings Breakdown" panel: one tile per earning bucket (a comp plan, or
 * a registered sub-bucket like the Mentor's Bonus rider), grouped by currency.
 * Enabled-plan filtering, ordering, per-row visibility, and "Always show"
 * ₱0.00 fabrication all happen server-side (admin-arranged via Settings →
 * Earnings breakdown), so this renders the rows in the order received. Hidden
 * entirely when the server returns no rows.
 *
 * When `slotId` is provided, tiles with at least one ledger entry become
 * clickable and open a dialog listing the individual entries behind that
 * earning type (for the active slot), with the type total. Fabricated zero
 * tiles have nothing to drill into, so they stay inert.
 */
function fmtAmount(currencyKind: string, amount: string, coinLabel: string): string {
  if (currencyKind === 'gc') return `${fmtCurrency(amount)} GC`;
  if (currencyKind === 'coin') return `${fmtNumber(amount)} ${coinLabel}`;
  if (currencyKind === 'token') return `${fmtNumber(amount)} Tokens`;
  return fmtCurrency(amount);
}

/**
 * Tiles are grouped by (plan, currency), so a plan that pays in two currencies
 * (e.g. Binary cash + the 5th-pair GC) would otherwise render two identically
 * labelled "Binary" tiles. Suffix the currency on non-cash tiles to keep them
 * distinct — same rule the give_me_gc wallet entries use.
 */
function displayLabel(label: string, currencyKind: string, coinLabel: string): string {
  if (currencyKind === 'cash') return label;
  const suffix = currencyKind === 'gc' ? 'GC' : currencyKind === 'coin' ? coinLabel : 'Tokens';
  return `${label} (${suffix})`;
}

export function EarningsBreakdownPanel({
  entries,
  coinLabel = 'Coins',
  columns = 2,
  slotId = null,
  planLabels,
  kindLabels,
  legacyOffset = null,
}: {
  entries: EarningsBreakdownEntry[];
  coinLabel?: string;
  /** 2 for the narrow dashboard rail, 3 for the wider Earnings page. */
  columns?: 2 | 3;
  /** Active slot — when set, tiles drill into the per-type ledger. */
  slotId?: string | null;
  /** Admin label overrides, passed through to the drill-in statement list. */
  planLabels?: Record<string, string>;
  kindLabels?: Record<string, string>;
  /**
   * Imported legacy earnings with no per-plan detail. These tiles are built from
   * ledger credits, so on a migrated slot they sum to LESS than the headline
   * "Lifetime earned" by exactly this amount. Pass it to render the reconciling
   * footnote; null/0 renders nothing.
   */
  legacyOffset?: string | null;
}) {
  const [selected, setSelected] = useState<EarningsBreakdownEntry | null>(null);
  if (entries.length === 0) return null;
  const legacyNote = legacyOffset != null && Number(legacyOffset) > 0 ? legacyOffset : null;
  const wideCols = columns === 3 ? 'lg:grid-cols-3' : '';
  const clickable = !!slotId;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Earnings Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid grid-cols-2 gap-3 ${wideCols}`}>
            {entries.map((e) => {
              // Fabricated "Always show" rows have no entries — nothing to drill into.
              const tileClickable = clickable && e.entryCount > 0;
              const inner = (
                <>
                  <div className="text-caption font-medium text-muted-foreground">
                    {displayLabel(e.label, e.currencyKind, coinLabel)}
                  </div>
                  <div
                    className={`mt-1 text-lg font-semibold tabular-nums${e.entryCount === 0 ? ' text-muted-foreground' : ''}`}
                  >
                    {fmtAmount(e.currencyKind, e.totalCredit, coinLabel)}
                  </div>
                </>
              );
              const cls = 'rounded-lg border bg-muted/30 p-4 text-center';
              return tileClickable ? (
                <button
                  key={`${e.key}:${e.currencyKind}`}
                  type="button"
                  onClick={() => setSelected(e)}
                  className={`${cls} transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                >
                  {inner}
                </button>
              ) : (
                <div key={`${e.key}:${e.currencyKind}`} className={cls}>
                  {inner}
                </div>
              );
            })}
          </div>
          {legacyNote ? (
            <p className="mt-4 text-caption text-muted-foreground">
              These tiles cover earnings from this system onward. Your Lifetime earned total also
              includes {fmtCurrency(legacyNote)} carried over from the previous system, which
              isn&apos;t itemized by type.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <EarningsBreakdownDetailDialog
        entry={selected}
        slotId={slotId}
        coinLabel={coinLabel}
        planLabels={planLabels}
        kindLabels={kindLabels}
        onClose={() => setSelected(null)}
      />
    </>
  );
}

/** Dialog listing the ledger entries behind one earning type, with its total. */
function EarningsBreakdownDetailDialog({
  entry,
  slotId,
  coinLabel,
  planLabels,
  kindLabels,
  onClose,
}: {
  entry: EarningsBreakdownEntry | null;
  slotId: string | null;
  coinLabel: string;
  planLabels?: Record<string, string>;
  kindLabels?: Record<string, string>;
  onClose: () => void;
}) {
  const open = !!entry && !!slotId;
  const ledgerQ = useQuery({
    queryKey: ['ledger', slotId, 'plan', entry?.sourcePlan, entry?.key, entry?.currencyKind],
    queryFn: () =>
      getLedger({ slotId: slotId ?? undefined, plan: entry!.sourcePlan, pageSize: 100 }),
    enabled: open,
  });
  // The breakdown groups by (bucket key, currency); the ledger endpoint filters
  // by plan only, so narrow client-side: this tile's currency, plus the kind
  // split — a sub-bucket tile (e.g. Mentor's Bonus) lists only its own kind,
  // while the host plan's tile excludes every registered sub-bucket kind. A
  // Give Me per-setting split tile narrows further to its own setting label;
  // the merged (labelless) Give Me tile lists only entries with no label.
  const rows = (ledgerQ.data ?? []).filter((r) => {
    if (r.currencyKind !== entry?.currencyKind) return false;
    if (entry?.sourcePlan === 'give_me') {
      const label = r.metadata?.setting_label;
      const hasLabel = typeof label === 'string' && label.trim() !== '';
      return entry.settingLabel ? hasLabel && label === entry.settingLabel : !hasLabel;
    }
    return entry?.kind ? r.kind === entry.kind : !EARNINGS_SUB_BUCKET_KINDS.has(r.kind);
  });
  const total = entry ? fmtAmount(entry.currencyKind, entry.totalCredit, coinLabel) : '';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {entry ? displayLabel(entry.label, entry.currencyKind, coinLabel) : 'Earnings'}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto">
          <StatementList
            entries={rows}
            kindLabels={kindLabels}
            planLabels={planLabels}
            title={`Total: ${total}`}
            subtitle={
              entry
                ? `${entry.entryCount} ${entry.entryCount === 1 ? 'entry' : 'entries'}`
                : undefined
            }
            emptyText={ledgerQ.isLoading ? 'Loading…' : 'No entries for this earning type yet.'}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
